'use client'

import type { Data } from '@repo/strapi'
import NextScript from 'next/script'
import { useEffect, useRef } from 'react'
import { Container, type ContainerProps } from '@/components/elementary/Container'
import { SCRIPT_TEST_IDS } from '@/lib/test-ids'

type GreenhouseGlobal = {
	Grnhse?: {
		Iframe?: {
			autoLoad?: () => void
		}
	}
}

const GREENHOUSE_ID = 'greenhouse'

// Helper to initialize Greenhouse iframe
function initializeGreenhouse() {
	const greenhouse = (window as Window & GreenhouseGlobal).Grnhse
	const autoLoad = greenhouse?.Iframe?.autoLoad

	if (autoLoad) {
		autoLoad()
	}
}

export function Script({
	component,
	containerPaddingX,
}: {
	readonly component: Data.Component<'global.script'>
	readonly containerPaddingX?: ContainerProps['paddingX']
}) {
	const { code, anchorId } = component
	const containerRef = useRef<HTMLDivElement>(null)

	// Extract script URL from code
	const scriptUrlMatch = code?.match(/src="([^"]+)"/)
	const scriptUrl = scriptUrlMatch?.[1]
	const isGreenhouseScript = scriptUrl?.includes(GREENHOUSE_ID)

	useEffect(() => {
		if (!containerRef.current || !code) return

		// Set HTML structure (without script tag)
		const htmlWithoutScript = code.replace(/<script[^>]*>.*?<\/script>/gi, '')
		containerRef.current.innerHTML = htmlWithoutScript

		// Re-initialize greenhouse iframe on spa navigation
		if (isGreenhouseScript) {
			initializeGreenhouse()
		}
	}, [code, isGreenhouseScript])

	if (!code) {
		return null
	}

	return (
		<Container paddingX={containerPaddingX} tag="section" id={anchorId ?? ''}>
			<div
				ref={containerRef}
				data-test-id={SCRIPT_TEST_IDS.COMPONENT}
				// Third-party scripts expected to modify the DOM
				suppressHydrationWarning
			/>
			{scriptUrl && (
				<NextScript
					src={scriptUrl}
					strategy="afterInteractive"
					onLoad={isGreenhouseScript ? initializeGreenhouse : undefined}
				/>
			)}
		</Container>
	)
}
